import { Stage } from '../courses';
import { CommonRequestOptions } from '../makeRequest';
import {
   CourseAssignmentDetails,
   StageCompletionRecord,
   getTeamCourseAssignmentDetails,
} from './get-course-assignment-details';

// Course assignment with several calculated attributes
export type CourseAssignmentTeamDetails = CourseAssignmentDetails & {
   overallCompletion: number;
   stageCompletionRecords: TeamStageCompletionRecord[];
   detailsType: 'team';
};

export function isCourseAssignmentTeamDetails(x: any): x is CourseAssignmentTeamDetails {
   return x?.detailsType === 'team';
}

export async function getCourseAssignmentTeamDetails(
   course_assignmentid: number | null,
   common?: CommonRequestOptions
) {
   if (course_assignmentid === null) return null;
   const courseAssignment = await getTeamCourseAssignmentDetails(course_assignmentid, common);
   return calculateCourseAssignmentTeamDetails(courseAssignment);
}

function calculateCourseAssignmentTeamDetails(
   courseAssignment: CourseAssignmentDetails
): CourseAssignmentTeamDetails {
   return {
      ...courseAssignment,
      detailsType: 'team',
      overallCompletion: calculateOverallCompletion(courseAssignment),
      stageCompletionRecords: calculateStageCompletionRecords(courseAssignment),
   };
}

function calculateOverallCompletion(courseAssignment: CourseAssignmentDetails) {
   return percentage(
      calculateCompletedStageCount(courseAssignment),
      calculateTotalStageCount(courseAssignment)
   );
}

function calculateTotalStageCount(courseAssignment: CourseAssignmentDetails) {
   return courseAssignment.completion_status.reduce((total, userCompletionStatus) => {
      return total + userCompletionStatus.stages.length;
   }, 0);
}

function calculateCompletedStageCount(courseAssignment: CourseAssignmentDetails) {
   return courseAssignment.completion_status.reduce((total, userCompletionStatus) => {
      const { stages } = userCompletionStatus;
      return total + reduceUserCompletionStages(stages);
   }, 0);
}

function percentage(numerator: number, denomenator: number): number {
   return denomenator ? Math.round((numerator / denomenator) * 100) : 0;
}

const reduceUserCompletionStages = (stages: Array<StageCompletionRecord>): number => {
   return stages.reduce((total, stage) => {
      return total + (stage.status === 'completed' ? 1 : 0);
   }, 0);
};

export type TeamStageCompletionRecord = {
   completionRecordType: 'team';
   completionCount: number;
   percentComplete: number;
   totalMembers: number;
   stage: Stage;
};

export function isTeamStageCompletionRecord(x: any): x is TeamStageCompletionRecord {
   return x && x.completionRecordType === 'team';
}

function calculateStageCompletionRecords(courseAssignment: CourseAssignmentDetails) {
   type StageSummaryMap = {
      [key: string]: TeamStageCompletionRecord;
   };

   const stageSummaries: StageSummaryMap = {};

   const memberCount = courseAssignment.completion_status
      ? courseAssignment.completion_status.length
      : 0;

   courseAssignment.course.stages.forEach(stage => {
      const newStageSummary: TeamStageCompletionRecord = {
         completionRecordType: 'team',
         completionCount: 0,
         totalMembers: memberCount,
         percentComplete: percentage(0, memberCount),
         stage: stage,
      };

      // @ts-ignore
      stageSummaries[stage.course_stageid] = newStageSummary;
   });

   courseAssignment.completion_status.forEach(userCompletionStatus => {
      userCompletionStatus.stages.forEach(userStage => {
         if (userStage.status === 'completed') {
            const stageSummary = stageSummaries[userStage.stage.course_stageid];
            stageSummary.completionCount += 1;
            stageSummary.percentComplete = percentage(
               stageSummary.completionCount,
               stageSummary.totalMembers
            );
         }
      });
   });

   return Object.values(stageSummaries);
}
